/* Ownership reconciliation (CID v1_742).
 *
 * ONE deterministic step that turns the ownership signals on a case into a
 * resolved structure, so a corporate holder is recorded as an INTERMEDIATE and
 * the natural person behind it is the UBO, with indirect ownership and the path
 * shown, on every generation path (documents-first assembly and reprocess, the
 * retier that finalises interview submits and officer regenerates).
 *
 * The person-to-intermediate leg is NEVER read from free text. It comes from
 * the structured document extraction: an enumerating document's registerExtract
 * names the SUBJECT entity it enumerates and, per holder, the entity held. A PSC
 * extract of a parent therefore emits "holder -> parent" with the document as
 * source, not "holder -> applicant". ownershipLinksFromExtracts builds those
 * legs; reconcileOwnership feeds them (plus the model's ownershipChain and any
 * direct ubos holdings) to the existing resolveOwnership engine and writes back
 * ubos (natural beneficial owners, indirect + path), intermediaries (the
 * corporate nodes) and the complete ownershipChain.
 */
import { resolveOwnership, looksCorporate, pctOf, pctIsFloor } from './ownership-resolution.js';
import { samePerson } from './person-name-match.js';
import { caseOwnershipThreshold } from './ownership-threshold.js';
import { parseHoldingLine, looksLikePersonName, sameCompany, entityName, seriesOf, statedShare } from './holding-line.js';

const clean = (v) => String(v || '').trim();
// Suffix-stripping normalisation, IDENTICAL to the resolveOwnership engine's own
// norm (ownership-resolution.js). This layer must agree with the engine on
// entity identity, or a holder named "Creative Agentics" on one document and
// "Creative Agentics Limited" on another is treated as two companies here (its
// stated percentage stranded on one variant, its type misread on the other) and
// then unified by the engine, so the reconcile and the resolution disagree.
const norm = (v) => clean(v).toLowerCase().replace(/\s+/g, ' ').replace(/[.,]/g, '')
  .replace(/\b(limited|ltd|plc|llp|llc|inc|corp|corporation|gmbh|ag|sa|sarl|bv|nv|oy|ab|aps|pte|pty)\b/g, '').replace(/\s+/g, ' ').trim();

/**
 * Structured ownership legs from per-document register extracts (v1_742).
 * Each enumerating registerExtract carries subjectEntity (the entity the
 * document enumerates) and, per holder, an optional `held` (defaults to
 * subjectEntity). One leg per holder, with the document as source. Never
 * invents a leg and never reads prose.
 *
 * @param extracts array of registerExtract objects (or { docType/name, registerExtract })
 * @param applicant applicant legal name (subjectEntity default when absent)
 */
export function ownershipLinksFromExtracts(extracts = [], applicant = '') {
  return readHoldings(extracts, applicant).legs;
}

// v1_862: the holders a document records as CEASED ("Karim Dasti (ceased 24
// October 2023)" on a Companies House PSC extract): never a current owner, kept
// as a former holder of the entity with the date the document gives.
export function formerHoldersFromExtracts(extracts = [], applicant = '') {
  return readHoldings(extracts, applicant).former;
}

// v1_862: a document ABOUT the applicant whose holding lines name held
// companies none of which is the applicant (a trading name or an abbreviation
// the applicant test cannot match). Nothing is dropped silently: each such
// document is named with the held names it gave, for a data quality line.
export function unmatchedHoldingDocuments(extracts = [], applicant = '') {
  return readHoldings(extracts, applicant).unmatched;
}

// v1_862: each holding line read as holder, percentage and held company
// (src/shared/holding-line.js). A chart line kept whole as the holder read as
// a company (it contains "Ltd") and every line was recorded against the
// applicant, so the natural persons only step left no beneficial owner (Next
// Markets, production 28/09/26). Now:
//   the held company is the one the line names, else the reader's held, else
//     the document's subject; a line about another company is a holding of
//     that company, never of the applicant;
//   the company test runs on the parsed holder name: a person's name with a
//     percentage is a natural person holder whatever the rest of the line says
//     (a type the reader declared for the whole line is not the holder's);
//   an indirect summary ("X — A and B via Y") and a piece of a line cut inside
//     its brackets are not holdings;
//   a ceased holder is recorded as former, never as a current holding.
// v1_867: one name per series. A bare "Brackmoor LLC" (a register's subject,
// a chart's box, "Brackmoor LLC (St. Kitts & Nevis)") is the series that
// holds the applicant when exactly one series of that LLC does; with none or
// several, the bare name is left as written.
function unifySeries(legs, applicant) {
  const baseOf = (n) => norm(entityName(n).replace(/\bseries\s+[a-z0-9]+\b/i, ''));
  const holdingSeries = new Map();
  for (const l of legs) {
    if (!seriesOf(l.holder) || !(sameCompany(l.held, applicant) || norm(l.held) === norm(applicant))) continue;
    const b = baseOf(l.holder);
    if (!holdingSeries.has(b)) holdingSeries.set(b, new Set());
    holdingSeries.get(b).add(norm(entityName(l.holder)));
  }
  const pick = new Map();
  for (const l of legs) {
    if (seriesOf(l.holder) && holdingSeries.has(baseOf(l.holder)) && holdingSeries.get(baseOf(l.holder)).size === 1) pick.set(baseOf(l.holder), entityName(l.holder));
  }
  const to = (n) => { const e = entityName(n).replace(/\s*\([^()]*\)\s*$/, ''); return !seriesOf(e) && pick.has(baseOf(e)) ? pick.get(baseOf(e)) : n; };
  return legs.map(l => ({ ...l, holder: l.holderType === 'entity' ? to(l.holder) : l.holder, held: to(l.held) }));
}

// v1_867: one name per natural person across the documents. "Aaron Vane"
// on the operating agreement and "Aaron Ellis Vane" on the register are the
// same person (samePerson); each reads as the fullest name the file gives, so
// the resolver does not count one owner twice.
function unifyPeople(legs) {
  const people = [];
  for (const l of legs) if (l.holderType === 'individual' && !people.some(n => n === l.holder)) people.push(l.holder);
  const canon = (n) => people.filter(x => samePerson(x, n)).sort((a, b) => b.split(/\s+/).length - a.split(/\s+/).length || b.length - a.length)[0] || n;
  return legs.map(l => l.holderType === 'individual' ? { ...l, holder: canon(l.holder) } : l);
}

// v1_877: the company a document's signatory acts for, where a role names one
// ("Director, signatory for and on behalf of Pearl Pay Limited"). Counted only
// when the signatory is an officer of that company (director, chief executive,
// secretary, officer, owner) and it is an ordinary company: a registered agent,
// trust or corporate service company signing a formation pack for its client
// ("Authorised signatory for and on behalf of Palmcove Trust Nevis Limited") is
// acting as agent, and its client stays the document's subject.
const SIGNED_FOR = /\b(?:on behalf of|signatory for|signing for)\s+(?:the company\s+)?([A-Z0-9][^;,()\n]*?\b(?:limited|ltd\.?|llc|inc\.?|plc|gmbh|ag|s\.r\.o\.|pty\.?\s+ltd\.?|b\.v\.|corp\.?|corporation))(?=[\s.,;)]|$)/i;
const SIGNING_OFFICER = /\b(director|chief executive|ceo|managing|secretary|officer|president|owner|ubo)\b/i;
const SERVICE_COMPANY = /\b(trust|trustees?|nominees?|registered agents?|agents?|corporate services?|formations?|fiduciar\w*|secretarial|incorporations?|management services)\b/i;
export function signedForCompany(rx = {}) {
  for (const p of ((rx && rx.peopleNamed) || [])) {
    const role = clean(p && p.role);
    const m = role.match(SIGNED_FOR);
    const name = m && clean(m[1]).replace(/^(?:and\s+)?on behalf of\s+/i, '').replace(/\.$/, '');
    if (!name) continue;
    if (!SIGNING_OFFICER.test(role.slice(0, m.index))) continue;
    if (SERVICE_COMPANY.test(name) || THIRD_PARTY.test(name)) continue;
    return name;
  }
  return null;
}

// v1_877: the chain carried from the previous report. Each line is read with
// the same holding line parser as the documents, because an earlier build
// stored whole chart lines as holders ("Ivan Morozski, 100% of Harbour Quay Ltd
// (UK, ...)", typed entity), pieces cut inside brackets, indirect summaries and
// ceased holders. Carried unread, they came back on every Reprocess as
// corporate holders of the applicant, fired the v1_744 conflict against the
// real natural person holder and emptied the UBO list (case cb0182a8,
// production 30/09/26). A piece, a summary or a ceased line is dropped; a whole line
// becomes its holder, percentage and held company.
export function cleanCarriedChain(legs = []) {
  const out = [];
  for (const l of (Array.isArray(legs) ? legs : [])) {
    if (!l || !clean(l.holder)) continue;
    // A line the earlier register step wrote ("board") is rebuilt from the
    // documents on every run; carried, it is a second copy at best.
    if (/^board$/i.test(clean(l.source))) continue;
    const p = parseHoldingLine(clean(l.holder), l.status);
    // An indirect summary ("X (in A, via B)", "X, A and B via C") restates a
    // path the documents give leg by leg; read as a direct holding it fires a
    // false conflict (Lynden, query 47, 01/10/26).
    if (p.fragment || p.heldList || p.via || p.status === 'ceased') continue;
    if (!p.parsed) { out.push(l); continue; }
    const person = looksLikePersonName(p.holder);
    out.push({
      ...l,
      holder: p.holder,
      held: p.held || clean(l.held),
      percentage: p.percentage != null ? p.percentage : l.percentage,
      holderType: person === true ? 'individual' : person === false ? 'entity' : (looksCorporate(p.holder) ? 'entity' : 'individual'),
    });
  }
  return out;
}

// v1_877: the Risk Factor title for an ownership data quality line.
export function dataQualityFactor(line) {
  return / is signed for .+, not /.test(String(line || '')) ? 'Ownership document signed for another company' : 'Ownership document does not name the applicant';
}

// v1_877: documents signed for another company, as a data quality line.
export function otherSubjectLines(docs = [], applicant = '') {
  return (docs || []).map(d => `${d.source} is signed for ${d.signedFor}, not ${applicant}${d.recordedSubject && norm(d.recordedSubject) !== norm(d.signedFor) ? ` (the reading recorded its subject as ${d.recordedSubject})` : ''}. Its holdings are read as holdings of ${d.signedFor}, not of ${applicant}. If it is meant to evidence who owns ${applicant}, ask the client for a chart signed for ${applicant}.`);
}
export function otherSubjectDocuments(extracts = [], applicant = '') {
  return readHoldings(extracts, applicant).otherSubject;
}

function readHoldings(extracts = [], applicant = '') {
  const legs = [];
  const former = [];
  const unmatched = [];
  const otherSubject = [];
  for (const raw of (Array.isArray(extracts) ? extracts : [])) {
    const rx = raw && raw.registerExtract ? raw.registerExtract : raw;
    if (!rx || rx.isEnumeratingDocument !== true) continue;
    const source = clean(raw && (raw.docType || raw.name || raw.docName)) || clean(rx.source) || 'register extract';
    // v1_877: a document signed for ANOTHER company ("Director, signatory for
    // and on behalf of Pearl Pay Limited") is that company's document, whatever
    // subject the reader recorded: a holding line that names no held company is
    // a holding of the company it was signed for, never of the applicant. A line
    // that names the applicant as held still counts. The document is named on
    // the case (otherSubject), never dropped silently.
    const signedFor = signedForCompany(rx);
    const signedForOther = !!(signedFor && applicant && !sameCompany(signedFor, applicant) && norm(signedFor) !== norm(applicant));
    const subject = signedForOther ? signedFor : (clean(rx.subjectEntity) || clean(applicant));
    if (signedForOther) otherSubject.push({ source, signedFor, recordedSubject: clean(rx.subjectEntity) || null });
    const aboutApplicant = !!applicant && (sameCompany(subject, applicant) || norm(subject) === norm(applicant));
    let reachesApplicant = false;
    const namedHeld = [];
    for (const h of (rx.holdersNamed || [])) {
      const text = clean(h && h.holder);
      if (!text || text === '...') continue;
      const p = parseHoldingLine(text, h && h.status);
      if (p.fragment || p.heldList) continue;
      // v1_867: a company holder's registration bracket ("KRTM LLC (CA, USA,
      // ID 202204318812)") is detail, not its name; a series stays in the name.
      const holder = p.parsed || !looksCorporate(p.holder, h && h.holderType) ? p.holder : entityName(p.holder).replace(/\s*\(([^()]*)\)\s*$/, '').trim() || p.holder;
      if (!holder) continue;
      const held = p.held || clean(h && h.held) || subject;
      if (!held) continue;
      if (norm(holder) === norm(held)) continue;
      const stated = statedShare(h && h.percentage);
      const percentage = p.percentage != null ? p.percentage : stated.percentage;
      if (p.status === 'ceased') {
        if (!former.some(f => norm(f.name) === norm(holder) && norm(f.held) === norm(held))) former.push({ name: holder, held, percentage, ceasedOn: p.ceasedOn || clean(h && h.ceasedOn) || null, source });
        continue;
      }
      if (applicant && (sameCompany(held, applicant) || norm(held) === norm(applicant))) reachesApplicant = true;
      else if (p.parsed && p.held && !namedHeld.some(x => norm(x) === norm(p.held))) namedHeld.push(p.held);
      // The company test on the parsed holder name; where the name cannot tell
      // (one word, or more than eight), the type the reader declared decides.
      const person = p.parsed ? looksLikePersonName(holder) : null;
      const declared = clean(h && h.holderType).toLowerCase();
      const holderType = person === true ? 'individual'
        : person === false ? 'entity'
        : p.parsed ? (declared === 'individual' || declared === 'person' ? 'individual' : declared === 'entity' || declared === 'corporate' ? 'entity' : (looksCorporate(holder) ? 'entity' : 'individual'))
        : (looksCorporate(holder, h.holderType) ? 'entity' : 'individual');
      legs.push({
        holder, held,
        // v1_744: the structured basis signal ("direct" / "indirect") travels
        // with the leg so an indirect natural-person holder is not treated as a
        // direct holder of the applicant.
        basis: p.basis === 'indirect' ? 'indirect' : clean(h && h.basis).toLowerCase(),
        percentage,
        ...(stated.voting != null ? { votingPercentage: stated.voting } : (pctOf(h && h.votingPercentage) != null ? { votingPercentage: clean(h.votingPercentage) } : {})),
        holderType,
        source, verified: true,
      });
    }
    if (aboutApplicant && !reachesApplicant && namedHeld.length) unmatched.push({ source, held: namedHeld });
  }
  return { legs: unifyPeople(unifySeries(legs, applicant)), former, unmatched, otherSubject };
}

// Third parties named on a corporate document who are NOT its subject: the
// registrar, the ACSP that verified an identity, the notary, the auditor, the
// bank. Never the subject entity of a register or PSC page.
const THIRD_PARTY = /\b(registrar|companies house|acsp|authorised corporate service provider|rapid formations|hmrc|notar|auditor|solicitor|revolut|bank|nominee service|formations?)\b/i;
const CO = "([A-Z][A-Za-z0-9&.'-]*(?:\\s+[A-Z][A-Za-z0-9&.'-]*){0,4}\\s+(?:Limited|Ltd|PLC|LLP|LLC|Inc|Incorporated|Corp|Corporation|GmbH|AG|Sarl|BV|NV|Pte|Pty|Holdings?|Group))";
// The document is ABOUT a company only when its text FRAMES that company as the
// subject of a register / PSC / officers page: "register of members of X",
// "PSC page for X", "X - people with significant control". Merely NAMING a
// company as a holder (a subscriber on the applicant's own memorandum) is not
// the subject, so this never mis-attributes the applicant's own register.
const SUBJECT_FRAMINGS = [
  new RegExp("(?:register (?:of members|of directors|extract)|persons?\\s+with\\s+significant\\s+control|people\\s+with\\s+significant\\s+control|psc\\s+(?:page|register|filing)|certificate of incumbency|officers?\\s+page)\\s+(?:page\\s+)?(?:for|of)\\s+" + CO, "i"),
  new RegExp(CO + "\\s*[-\\u2013\\u2014:]\\s*(?:people|persons?)\\s+with\\s+significant\\s+control", "i"),
];

/**
 * Deterministic subject-entity resolution (v1_744). If an enumerating document's
 * own text (its docType/title and keyFindings) FRAMES a non-applicant company as
 * the entity whose register or PSC page this is, THAT company is the subject
 * entity, overriding an empty or wrong model value. Conservative: it fires only
 * on an explicit subject framing, never on a company merely named as a holder,
 * so the applicant's own memorandum (which names its corporate subscriber) is
 * never re-attributed. Returns what it found and whether it overrode the model.
 *
 * @returns { subjectEntity, overrode, modelValue }
 */
export function resolveSubjectEntity(analysis = {}, applicant = '') {
  const rx = (analysis && analysis.registerExtract) || analysis || {};
  const modelValue = clean(rx.subjectEntity);
  const appN = norm(applicant);
  const text = [analysis && analysis.docType, ...((analysis && analysis.keyFindings) || [])].filter(Boolean).join(' \n ');
  let headerCompany = '';
  for (const re of SUBJECT_FRAMINGS) {
    const m = text.match(re);
    const name = m && clean(m[1]);
    if (name && norm(name) !== appN && !THIRD_PARTY.test(name)) { headerCompany = name; break; }
  }
  if (headerCompany) {
    return { subjectEntity: headerCompany, overrode: !!modelValue && norm(modelValue) !== norm(headerCompany), modelValue };
  }
  // No decisive framing: keep the model's value, or default to the applicant.
  return { subjectEntity: modelValue || clean(applicant), overrode: false, modelValue };
}

// v1_744: a per-case corporate registry, so a company named without its suffix
// on one document ("Creative Agentics") is not read as a natural person because
// the suffix ("Limited") that would classify it sits on a different document, or
// as a separate entity from its own suffixed form. A normalised name is
// corporate when ANY signal on the case says so: it looks corporate on any leg,
// it is the subject entity of an enumerating extract, or it is the entity HELD
// by a leg (natural persons are only ever holders; only companies are held). The
// registry keys on the suffix-stripped norm, so every variant of the same
// company shares one entry. Reclassifying a natural-looking leg to a company
// keeps a corporate holder OFF the UBO list and out of a false conflict; it
// never promotes a real person, because a person is never held and never carries
// a corporate suffix on any document.
function corporateKeysFor(legs, extracts, applicantN) {
  const keys = new Set();
  for (const raw of (Array.isArray(extracts) ? extracts : [])) {
    const rx = raw && raw.registerExtract ? raw.registerExtract : raw;
    if (!rx || rx.isEnumeratingDocument !== true) continue;
    const s = norm(rx.subjectEntity);
    if (s && s !== applicantN) keys.add(s);
  }
  for (const l of (legs || [])) {
    if (l.holderType === 'entity' || looksCorporate(l.holder, l.holderType)) keys.add(norm(l.holder));
    const heldN = norm(l.held);
    if (heldN && heldN !== applicantN) keys.add(heldN);
  }
  return keys;
}
function reclassifyLegs(legs, corpKeys) {
  return (legs || []).map(l => corpKeys.has(norm(l.holder)) ? { ...l, holderType: 'entity' } : l);
}

// v1_744: an indirect natural-person holder of the applicant does NOT hold it
// directly. Re-point their leg through the applicant's single direct CORPORATE
// holder (the intermediate), from the structured basis:indirect signal, and drop
// the flattening direct-to-applicant leg. When there is no single corporate
// holder to route through, the person is left as an unresolved indirect holder.
function resolveIndirectFromExtracts(rawLegs, applicant) {
  const appN = norm(applicant);
  const directCorp = [];
  for (const l of rawLegs) {
    if (norm(l.held) === appN && l.holderType === 'entity' && l.basis !== 'indirect' && !directCorp.some(c => norm(c) === norm(l.holder))) directCorp.push(l.holder);
  }
  const indirectNaturals = new Set(rawLegs.filter(l => norm(l.held) === appN && l.holderType === 'individual' && l.basis === 'indirect').map(l => norm(l.holder)));
  const out = [];
  const emitted = new Set();
  const unresolvedIndirect = [];
  for (const l of rawLegs) {
    const toApp = norm(l.held) === appN;
    const holderN = norm(l.holder);
    if (toApp && l.holderType === 'individual' && indirectNaturals.has(holderN)) {
      // Drop every direct/indirect leg this person has TO the applicant; emit one
      // leg through the intermediate instead.
      if (directCorp.length === 1) {
        const key = holderN + '>' + norm(directCorp[0]);
        if (!emitted.has(key)) { emitted.add(key); out.push({ holder: l.holder, held: directCorp[0], basis: 'indirect', percentage: l.percentage, holderType: 'individual', source: l.source, verified: true }); }
      } else if (!unresolvedIndirect.some(u => norm(u) === holderN)) {
        unresolvedIndirect.push(l.holder);
      }
      continue;
    }
    out.push(l);
  }
  return { legs: out, unresolvedIndirect };
}

// Merge legs, de-duplicating by holder+held. A register-sourced (verified) leg
// wins over an unverified one; otherwise the first seen stays. A later leg can
// fill an empty percentage on an existing one.
function mergeLegs(...groups) {
  const byKey = new Map();
  for (const group of groups) {
    for (const l of (group || [])) {
      if (!l || !clean(l.holder) || !clean(l.held)) continue;
      if (norm(l.holder) === norm(l.held)) continue;
      const key = norm(l.holder) + '>' + norm(l.held);
      const cur = byKey.get(key);
      if (!cur) { byKey.set(key, { ...l }); continue; }
      // v1_771: the same figure stated as a band on one copy ("75 percent or
      // more") and bare on the other ("75") keeps the band, whichever copy wins.
      const floorText = [l, cur].find(x => pctIsFloor(x.percentage) && pctOf(x.percentage) != null && pctOf(x.percentage) === pctOf(l.percentage) && pctOf(x.percentage) === pctOf(cur.percentage));
      if (l.verified && !cur.verified) { byKey.set(key, { ...l, ...(floorText ? { percentage: clean(floorText.percentage) } : {}) }); continue; }
      if (floorText) cur.percentage = clean(floorText.percentage);
      // v1_877: a band ("75% or more", the Companies House PSC band) and an
      // exact figure at or above its floor on another document (the register
      // of members: 100 of 100 shares) are the same holding; the exact figure
      // is the finer statement and is kept.
      else if (pctIsFloor(cur.percentage) && pctOf(cur.percentage) != null && pctOf(l.percentage) != null && !pctIsFloor(l.percentage) && pctOf(l.percentage) >= pctOf(cur.percentage) && pctOf(l.percentage) <= 100) cur.percentage = clean(l.percentage);
      // Prefer a stated numeric percentage over an empty or non-numeric one
      // ("at least one share" yields to "100%").
      if (pctOf(l.percentage) != null && pctOf(cur.percentage) == null) cur.percentage = clean(l.percentage);
      else if (!clean(cur.percentage) && clean(l.percentage)) cur.percentage = clean(l.percentage);
      if (!clean(cur.source) && clean(l.source)) cur.source = clean(l.source);
      // v1_867: a voting figure on either copy is kept, apart from the equity share.
      if (pctOf(cur.votingPercentage) == null && pctOf(l.votingPercentage) != null) cur.votingPercentage = clean(l.votingPercentage);
      // A basis:indirect signal on either copy is retained.
      if (l.basis === 'indirect' && cur.basis !== 'indirect') cur.basis = 'indirect';
    }
  }
  return [...byKey.values()];
}

// Direct holdings the model recorded on report.ubos become links holder ->
// applicant, so a flat structure (a natural person holding the applicant
// directly) resolves without any chain. A corporate ubos entry becomes a direct
// corporate leg; the extract legs above then carry the chain above it.
function legsFromUbos(ubos = [], applicant = '') {
  const legs = [];
  for (const u of (ubos || [])) {
    // v1_862: a holding line written as the name is read as one; a line about
    // another company, a summary or a cut piece is not a holding of the applicant.
    const p = parseHoldingLine(clean(u && u.name));
    if (p.fragment || p.heldList || p.status === 'ceased') continue;
    if (p.parsed && p.held && !sameCompany(p.held, applicant)) continue;
    const name = p.parsed ? p.holder : clean(u && u.name);
    if (!name || !applicant) continue;
    legs.push({
      holder: name, held: applicant,
      percentage: (u.ownership != null ? clean(u.ownership) : ''),
      ...(pctOf(u.votingRights) != null ? { votingPercentage: clean(u.votingRights) } : {}),
      holderType: (u.corporateUBO === true || looksCorporate(name, u.corporateUBO === false ? 'individual' : undefined)) ? 'entity' : 'individual',
      source: clean(u.source) || 'board', verified: false,
    });
  }
  return legs;
}

function priorUboByName(ubos, name) {
  return (ubos || []).find(u => u && u.name && samePerson(clean(u.name), clean(name))) || null;
}

// The intermediate names on a resolved party's shortest path: an entity that is
// BOTH held by someone and itself a holder is an intermediate (the applicant is
// only ever held; the natural person is only ever a holder).
function intermediariesOnPath(party) {
  const p = (party && party.paths && party.paths.slice().sort((a, b) => (a.depth || 99) - (b.depth || 99))[0]) || null;
  if (!p || !Array.isArray(p.chain)) return [];
  const holders = new Set(p.chain.map(l => norm(l && l.holder)).filter(Boolean));
  const names = [];
  for (const l of p.chain) {
    const held = clean(l && l.held);
    if (held && holders.has(norm(held))) names.push(held);
  }
  return [...new Set(names)];
}

/**
 * Reconcile the ownership structure on a report (v1_742). Pure: returns the new
 * ubos, intermediaries and ownershipChain; the caller writes them back.
 *
 * @param report the board/report
 * @param registerExtracts persisted per-document register extracts (structured)
 * @param opts { threshold }
 * @returns { ubos, intermediaries, ownershipChain, resolution }
 */
export function reconcileOwnership(report = {}, registerExtracts = [], opts = {}) {
  const r = report && typeof report === 'object' ? report : {};
  const applicant = clean(r.clientName) || clean(r.companyDetails && r.companyDetails.legalName) || clean(r.companyName);
  const priorUbos = Array.isArray(r.ubos) ? r.ubos.filter(Boolean) : [];
  // v1_830: the firm threshold passed by the engine, else the one the case was built under.
  const threshold = opts.threshold || caseOwnershipThreshold(r);

  // Structured legs (register extracts, then the model's ownershipChain) are
  // authoritative on WHAT a holder holds. A ubos entry becomes a direct
  // holder->applicant leg ONLY when no structured leg already places that
  // holder: otherwise the old flattening returns as a phantom direct leg
  // (Danielle -> applicant) double-counting against her real indirect leg
  // (Danielle -> Creative Agentics).
  const rawLegs0 = ownershipLinksFromExtracts(registerExtracts, applicant);
  // v1_744: reclassify a company named without its suffix as a company, using the
  // whole case's corporate signals, BEFORE anything reads holderType. Otherwise a
  // holding company written "Creative Agentics" on the group chart is read as a
  // natural person and either flattened onto the UBO list or fired as a false
  // ownership conflict against its own suffixed form on the memorandum.
  const rawExtractLegs = reclassifyLegs(rawLegs0, corporateKeysFor(rawLegs0, registerExtracts, norm(applicant)));
  // v1_744: re-point indirect natural-person holders through the single corporate
  // intermediate (the basis:indirect signal), dropping the flattening
  // direct-to-applicant leg.
  const { legs: extractLegs, unresolvedIndirect } = resolveIndirectFromExtracts(rawExtractLegs, applicant);
  // v1_877: a person named in the carried chain under a shorter or longer form
  // of a name the documents give ("Dylan Shub" and "Dylan Shaun Shub") is the
  // same person, under the documents' name.
  // Only real personal names are matched: a placeholder ("Shareholder 4
  // (identity not disclosed)") is never folded into another (Bloxcross).
  const docPeople = [...new Set(extractLegs.filter(l => l.holderType === 'individual' && looksLikePersonName(l.holder) === true).map(l => l.holder))];
  const chainLegs = cleanCarriedChain(Array.isArray(r.ownershipChain) ? r.ownershipChain : []).map(l => {
    if (l.holderType !== 'individual' || looksLikePersonName(l.holder) !== true) return l;
    const same = docPeople.find(n => n !== l.holder && samePerson(n, l.holder));
    return same ? { ...l, holder: same } : l;
  });
  // A holder placed by ANY structured leg (raw or re-pointed) does not also get a
  // flattening ubos direct leg.
  const covered = new Set([...rawExtractLegs, ...extractLegs, ...chainLegs].map(l => norm(l && l.holder)).filter(Boolean));
  // v1_883: a stored owner under another form of a name a structured leg
  // already places ("STEVEN BRODIE" beside "Mr Steven Anthony Brodie") is that
  // person, not a second owner flattened back in from the stored list (Fig
  // Markets). Only real personal names are matched, as above.
  const placedPeople = [...new Set([...rawExtractLegs, ...extractLegs, ...chainLegs]
    .filter(l => l && looksLikePersonName(clean(l.holder)) === true).map(l => clean(l.holder)))];
  const placedElsewhere = (u) => looksLikePersonName(clean(u.name)) === true
    && placedPeople.some(n => samePerson(n, clean(u.name)));
  let links = mergeLegs(
    extractLegs,
    chainLegs,
    legsFromUbos(priorUbos.filter(u => u && !covered.has(norm(u.name)) && !placedElsewhere(u)), applicant),
  );

  // Nothing to resolve (no applicant, or no links at all): leave ubos untouched
  // so a case with no ownership data is never blanked.
  if (!applicant || !links.length) {
    return { ubos: priorUbos, intermediaries: Array.isArray(r.intermediaries) ? r.intermediaries : [], ownershipChain: Array.isArray(r.ownershipChain) ? r.ownershipChain : [], resolution: null, conflict: null, shareholders: [], formerHolders: applicant ? formerHoldersFromExtracts(registerExtracts, applicant).filter(f => sameCompany(f.held, applicant)) : [], dataQuality: applicant ? [...unmatchedHoldingLines(unmatchedHoldingDocuments(registerExtracts, applicant), applicant), ...otherSubjectLines(otherSubjectDocuments(registerExtracts, applicant), applicant)] : [] };
  }

  // v1_810: A SHARE THE FILE ALREADY STATES. A register leg with no percentage
  // made its holder "share not stated", so the resolver kept them as a possible
  // beneficial owner with ownership '' (five holders of Bloxcross, production
  // 25/09/26), and wrote '' over a share the officer had set with Set share on
  // the next rebuild. Where the leg to the applicant states no share but the
  // file does for the same natural person (the officer's recorded share first,
  // then a stated share on the UBO list), that share is used, marked as taken
  // from the file. A holder below the threshold then resolves as a shareholder,
  // not a beneficial owner. A share stated nowhere stays unknown: an unknown
  // share is not a small share.
  // v1_817: the stored shareholders (board.shareholders, where R5 moves a holder
  // below the threshold, with the officer's source) are read too, and so is any
  // officer recorded share the caller passes from the previous report (a
  // Reprocess reconciles its fresh board before the officer state is carried).
  const priorShareholders = Array.isArray(r.shareholders) ? r.shareholders.filter(Boolean) : [];
  const priorOfficer = (Array.isArray(opts.priorShares) ? opts.priorShares : []).filter(u => u && u.name && clean(u.ownershipSource));
  const shareOnFile = (holder) => {
    const same = [...priorUbos, ...priorShareholders, ...priorOfficer].filter(u => u && u.name && samePerson(u.name, holder));
    const num = (u) => { const n = pctOf(u && u.ownership); return n == null ? null : n; };
    const officer = same.find(u => clean(u.ownershipSource) && num(u) != null);
    if (officer) return { pct: String(num(officer)), from: 'recorded by the officer', ownershipSource: clean(officer.ownershipSource) };
    const stated = same.find(u => num(u) != null);
    return stated ? { pct: String(num(stated)), from: 'recorded on the file' } : null;
  };
  links = links.map(l => {
    if (!l || norm(l.held) !== norm(applicant) || pctOf(l.percentage) != null || l.holderType === 'entity' || looksCorporate(l.holder, l.holderType)) return l;
    const f = shareOnFile(l.holder);
    return f ? { ...l, percentage: f.pct, source: clean(l.source) ? clean(l.source) + '; share as ' + f.from : 'share as ' + f.from, shareFrom: f.from, ...(f.ownershipSource ? { ownershipSource: f.ownershipSource } : {}) } : l;
  });

  // v1_744: OWNERSHIP CONFLICT. A corporate holder recorded as the applicant's
  // (sole) shareholder AND a natural person cannot both hold ~100% of the
  // applicant DIRECTLY: that is a contradiction between sources, not a structure.
  // When it survives the indirect re-pointing (no basis:indirect signal
  // reconciled them), record it for the officer, name both sources, drop the
  // natural person's flattening direct leg and leave the UBO UNRESOLVED rather
  // than assert a flat direct owner the file does not support.
  const appN = norm(applicant);
  const majorityOrUnknown = (p) => { const n = pctOf(p); return n == null || n >= 50; };
  const directToApp = links.filter(l => norm(l.held) === appN);
  const directCorp = directToApp.filter(l => l.holderType === 'entity' && majorityOrUnknown(l.percentage));
  const directNat = directToApp.filter(l => l.holderType === 'individual' && majorityOrUnknown(l.percentage));
  let conflict = null;
  if (directCorp.length >= 1 && directNat.length >= 1) {
    conflict = {
      kind: 'ownership_conflict',
      corporate: [...new Set(directCorp.map(l => l.holder))],
      natural: [...new Set(directNat.map(l => l.holder))],
      sources: [...new Set(directToApp.map(l => l.source).filter(Boolean))],
      factor: `Ownership sources conflict: ${[...new Set(directCorp.map(l => l.holder))].join(', ')} (a corporate shareholder) and ${[...new Set(directNat.map(l => l.holder))].join(', ')} are each recorded as holding a controlling stake in ${applicant} DIRECTLY. Both cannot hold it directly; one source describes an indirect holding through the other. Sources: ${[...new Set(directToApp.map(l => l.source).filter(Boolean))].join('; ') || 'the file'}. Beneficial ownership is left unresolved until this is reconciled.`,
    };
    // Drop the natural person's direct-to-applicant legs so they are not asserted
    // as a flat UBO; the corporate holder stays and reads as unresolved.
    const natNames = new Set(directNat.map(l => norm(l.holder)));
    links = links.filter(l => !(norm(l.held) === appN && l.holderType === 'individual' && natNames.has(norm(l.holder))));
  }
  // Indirect holders we could not route through an intermediate are also a
  // conflict to surface rather than flatten.
  if (!conflict && unresolvedIndirect && unresolvedIndirect.length) {
    conflict = { kind: 'ownership_unresolved_indirect', natural: unresolvedIndirect, corporate: [], sources: [], factor: `Recorded as an INDIRECT holder of ${applicant} but the intermediate company could not be identified from the file: ${unresolvedIndirect.join(', ')}. Beneficial ownership is left unresolved until the holding company is evidenced.` };
  }

  const resolution = resolveOwnership({ applicant, links, threshold });

  // UBOs: the resolved natural beneficial owners, each carrying whether they
  // hold directly or through intermediaries, the path in words, and the
  // intermediate names. Prior per-person fields (email, nationality, SoW, notes)
  // are preserved by name.
  const ubos = resolution.beneficialOwners.map((bo) => {
    const prior = priorUboByName(priorUbos, bo.name) || {};
    const indirect = (bo.depth || 1) > 1;
    const inter = indirect ? intermediariesOnPath(bo) : [];
    return {
      ...prior,
      name: bo.name,
      corporateUBO: false,
      beneficialOwner: true,
      // v1_771: a band on the path ("75 percent or more") stays a band: "75 or
      // more" (every percentage reader takes the leading number, unchanged).
      ownership: bo.uncertain ? '' : String(bo.effective) + (bo.atLeast ? ' or more' : ''),
      indirect,
      ownershipPath: indirect ? (bo.relationship || '') : '',
      intermediaries: inter,
      // v1_867: the voting rights, where a document states them apart from the
      // equity share, and which of the two met the threshold.
      ...(bo.votes != null ? { votingRights: String(bo.votes) } : {}),
      ...(bo.uncertain ? {} : { controlBasis: controlBasisText(bo, threshold) }),
    };
  });

  // Intermediaries: every corporate node on the structure (a holder that is a
  // company, or an entity that is itself held), recorded as a structural node,
  // NOT a UBO.
  const interByName = new Map();
  for (const p of resolution.parties) {
    if (!p.corporate) continue;
    interByName.set(norm(p.name), {
      name: p.name,
      holderType: 'entity',
      percentageEffective: p.uncertain ? '' : String(p.effective),
      resolvesTo: (resolution.beneficialOwners.find(bo => (bo.paths || []).some(pp => (pp.chain || []).some(l => norm(l.held) === norm(p.name)))) || {}).name || '',
      // Unresolved means a dead-end corporate holder: no leg names who holds IT,
      // so the chain cannot reach a natural person through this node. A node with
      // an inbound holder leg (Danielle -> Creative Agentics) is resolved.
      unresolved: !links.some(l => norm(l.held) === norm(p.name) && clean(l.holder)),
      source: (links.find(l => norm(l.holder) === norm(p.name) || norm(l.held) === norm(p.name)) || {}).source || '',
    });
  }
  const intermediaries = [...interByName.values()];

  // ownershipChain: every leg, present only when the structure actually passes
  // through an intermediate. A flat direct-natural-person structure keeps an
  // empty chain (the invariant the panel and the risk engine rely on), its
  // holder recorded on ubos.
  const ownershipChain = intermediaries.length
    ? links.map(l => ({ holder: l.holder, held: l.held, percentage: clean(l.percentage), ...(pctOf(l.votingPercentage) != null ? { votingPercentage: clean(l.votingPercentage) } : {}), holderType: l.holderType, source: l.source }))
    : [];

  // v1_810: the natural holders below the threshold with a stated share. They
  // are not beneficial owners and not on ubos, but they are shareholders of
  // the applicant, and the Directors and UBOs panel counts them as such.
  const shareholders = resolution.parties
    .filter(p => !p.corporate && !p.beneficialOwner && !p.uncertain)
    .map(p => { const leg = links.find(l => norm(l.holder) === norm(p.name)) || {}; return { name: p.name, ownership: String(p.effective), indirect: (p.depth || 1) > 1, source: leg.source || '', ...(leg.ownershipSource ? { ownershipSource: leg.ownershipSource } : {}) }; });

  // v1_862: holders the documents record as ceased, kept as former holders of
  // the applicant (never current owners), with the date the document gives.
  const formerHolders = formerHoldersFromExtracts(registerExtracts, applicant).filter(f => sameCompany(f.held, applicant) || norm(f.held) === norm(applicant))
    .filter(f => !ubos.some(u => samePerson(u.name, f.name)));

  // v1_862: documents about the applicant whose lines never name it (a
  // trading name or an abbreviation): named, never dropped silently.
  // v1_867: a document whose held companies include one that holds the
  // applicant is about that holder (an LLC's schedule of members the reader
  // filed under the applicant), not a trading name of the applicant.
  const holdersOfApp = new Set(links.filter(l => norm(l.held) === appN && l.holderType === 'entity').map(l => norm(l.holder)));
  const unmatchedDocs = unmatchedHoldingDocuments(registerExtracts, applicant).filter(d => !d.held.some(h => holdersOfApp.has(norm(entityName(h)))));
  const dataQuality = [...unmatchedHoldingLines(unmatchedDocs, applicant), ...otherSubjectLines(otherSubjectDocuments(registerExtracts, applicant), applicant)];

  return { ubos, intermediaries, ownershipChain, resolution, conflict, shareholders, formerHolders, dataQuality };
}

// v1_867: which figure made the person a beneficial owner. The test reads the
// higher of the equity share and the voting rights; the words say which applied.
export function controlBasisText(bo, threshold = 25) {
  const eq = String(bo.effective) + (bo.atLeast ? ' percent or more' : ' percent');
  // v1_874: where majority control of an intermediate was looked through, say so.
  const through = Array.isArray(bo.controlThrough) && bo.controlThrough.length
    ? ` Control was looked through ${bo.controlThrough.join(' and ')}: holding more than 50 percent of the votes of ${bo.controlThrough.length === 1 ? 'that company' : 'each'} counts as all of its votes in the company below.`
    : '';
  if (bo.testedOn === 'votes') {
    return `Beneficial owner through control of ${bo.votes} percent of the voting rights, at or above the ${threshold} percent threshold; the equity share is ${eq}, so the voting rights applied.` + through;
  }
  return `Beneficial owner through ${eq} of the equity, at or above the ${threshold} percent threshold` + (bo.votes != null ? `; the voting rights are ${bo.votes} percent, so the equity share applied.` : '.') + through;
}

// The data quality line for each such document.
export function unmatchedHoldingLines(unmatchedDocs = [], applicant = '') {
  return (unmatchedDocs || []).map(d => `${d.source} is about ${applicant} but none of its holding lines names ${applicant}: the companies it names as held are ${d.held.join('; ')}. If one of these is ${applicant} under a trading name or an abbreviation, record who holds ${applicant} on the case; until then these lines are not read as holdings of ${applicant}.`);
}
